import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  Grid,
  Paper,
  Avatar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { fetchReport, handleError, formatDate2 } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';

const NewVetUsersReport = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const snackbar = useSnackbar();
  
  useEffect(() => {
    fetchNewUsers(1);
  }, []);

  const fetchNewUsers = async (pageNumber) => {
    setIsLoading(true);
    try {
      const response = await fetchReport('NEW_VET_USERS', pageNumber);
      setData(response);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchNewUsers(value);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {data ? (
        <>
          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <PersonAddIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">
                {t('reports.newUsersTitle')} ({data.total_results || (data.content ? data.content.length : 0)})
              </Typography>
            </Box>
          </Box>
          
          {data.content && data.content.length > 0 ? (
            <>
              {data.content.map((user) => (
                <Paper 
                  key={user.id} 
                  elevation={2}
                  sx={{ 
                    p: 2, 
                    mb: 2, 
                    '&:hover': { boxShadow: 6 }
                  }}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    {/* Column 1: Avatar */}
                    <Grid item xs={12} sm="auto">
                      <Avatar
                        src={user.image_url}
                        alt={user.name}
                        sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}
                      >
                        {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                    </Grid>

                    {/* Column 2: User Info */}
                    <Grid item xs={12} sm>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {user.name || t('reports.noName')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                      {user.phone && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {t('reports.telPrefix')} {user.phone}
                        </Typography>
                      )}
                    </Grid>

                  </Grid>
                </Paper>
              ))}
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={3} mb={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3} component={Paper} variant="outlined">
              <PersonAddIcon color="action" sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary">
                {t('reports.noNewUsers')}
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('common.noData')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default NewVetUsersReport; 